import { useEffect, useRef } from 'react';

import { TOAST_RESUME_RATIO } from '../constants/toastTiming';
import { UseAutoHideTimerParams } from '../types/UseAutoHideTimerParams';

export const useAutoHideTimer = ({
  autoHideDuration,
  onClose,
  open,
}: UseAutoHideTimerParams): { pause: () => void; resume: () => void } => {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Read through a ref so a parent re-render with a new onClose does not restart the timer
  const onCloseRef = useRef(onClose);

  const start = (duration: number): void => {
    clearTimeout(timer.current);
    timer.current = setTimeout((): void => {
      onCloseRef.current();
    }, duration);
  };

  const pause = (): void => {
    clearTimeout(timer.current);
  };

  const resume = (): void => {
    if (open) {
      start(autoHideDuration * TOAST_RESUME_RATIO);
    }
  };

  useEffect((): void => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect((): (() => void) | undefined => {
    if (!open) {
      return undefined;
    }

    start(autoHideDuration);
    window.addEventListener('blur', pause);
    window.addEventListener('focus', resume);

    return (): void => {
      clearTimeout(timer.current);
      window.removeEventListener('blur', pause);
      window.removeEventListener('focus', resume);
    };
  }, [open, autoHideDuration]);

  return { pause, resume };
};
